import { useCallback, useEffect, useRef, useState } from "react"

import { usePostSearchFilters } from "openapi/api/endpoints"

import { type SearchFiltersDraft, type SimpleFilterData, simpleFiltersToDraft } from "@/lib/search-filter-model"

import { useDebounce } from "./useDebounce"

export type SearchAnalysisStatus = "idle" | "parsing" | "ready" | "parse_error"

const MAX_PARSE_ATTEMPTS = 3

export interface SearchPromptParseError {
  message: string
  prompt: string
}

interface UseSearchPromptAnalysisOptions {
  query: string
  onApplyFilters: (filters: SearchFiltersDraft) => void
  onResetFilters: () => void
}

/**
 * Handles debounced prompt parsing and stale-response protection for the
 * conversational preflight flow.
 */
export function useSearchPromptAnalysis({
  query,
  onApplyFilters,
  onResetFilters,
}: UseSearchPromptAnalysisOptions) {
  const trimmedQuery = query.trim()
  const debouncedQuery = useDebounce(trimmedQuery, 900)
  const latestQueryRef = useRef(trimmedQuery)
  const requestIdRef = useRef(0)
  const [acceptedPrompt, setAcceptedPrompt] = useState("")
  const [attemptCount, setAttemptCount] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)
  const [status, setStatus] = useState<SearchAnalysisStatus>(trimmedQuery ? "parsing" : "idle")
  const [parseError, setParseError] = useState<SearchPromptParseError | null>(null)

  const { mutateAsync: parseQuery } = usePostSearchFilters()

  useEffect(() => {
    latestQueryRef.current = trimmedQuery
  }, [trimmedQuery])

  useEffect(() => {
    if (!trimmedQuery) {
      requestIdRef.current += 1
      setAcceptedPrompt("")
      setAttemptCount(0)
      setIsRetrying(false)
      setParseError(null)
      setStatus("idle")
      onResetFilters()
      return
    }

    if (trimmedQuery !== acceptedPrompt) {
      setAttemptCount(0)
      setIsRetrying(false)
      setStatus("parsing")
      setParseError(null)
      setAcceptedPrompt("")
    }
  }, [acceptedPrompt, onResetFilters, trimmedQuery])

  const runAnalysis = useCallback(
    async (prompt: string) => {
      const normalizedPrompt = prompt.trim()

      if (!normalizedPrompt) {
        onResetFilters()
        setAcceptedPrompt("")
        setParseError(null)
        setStatus("idle")
        return undefined
      }

      const requestId = ++requestIdRef.current
      setStatus("parsing")
      setAttemptCount(0)
      setIsRetrying(false)
      setParseError(null)

      for (let attempt = 1; attempt <= MAX_PARSE_ATTEMPTS; attempt += 1) {
        setAttemptCount(attempt)
        setIsRetrying(attempt > 1)

        try {
          const parsedFilters = await parseQuery({ data: { prompt: normalizedPrompt } })
          const nextFilters = simpleFiltersToDraft(parsedFilters as SimpleFilterData)

          if (requestId !== requestIdRef.current || latestQueryRef.current !== normalizedPrompt) {
            return nextFilters
          }

          onApplyFilters(nextFilters)
          setAcceptedPrompt(normalizedPrompt)
          setAttemptCount(attempt)
          setIsRetrying(false)
          setStatus("ready")
          return nextFilters
        } catch (error) {
          if (requestId !== requestIdRef.current || latestQueryRef.current !== normalizedPrompt) {
            return undefined
          }

          if (attempt === MAX_PARSE_ATTEMPTS) {
            setAcceptedPrompt("")
            setAttemptCount(attempt)
            setIsRetrying(false)
            setParseError({
              message: "We couldn't analyze this query automatically. Edit the filters manually to continue.",
              prompt: normalizedPrompt,
            })
            setStatus("parse_error")
            throw error
          }
        }
      }

      return undefined
    },
    [onApplyFilters, onResetFilters, parseQuery],
  )

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery === acceptedPrompt) return
    void runAnalysis(debouncedQuery).catch(() => undefined)
  }, [acceptedPrompt, debouncedQuery, runAnalysis])

  return {
    acceptedPrompt,
    attemptCount,
    isRetrying,
    maxAttempts: MAX_PARSE_ATTEMPTS,
    parseError,
    status,
  }
}
